import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    Grid,
    TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import ApiService from "../../../services/categoryManagementApi";

const { reportsAndAnalyticsApi } = ApiService;

// UTC helpers
function toUTCStart(date) {
    if (!date) return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}
function toUTCEnd(date) {
    if (!date) return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}

const RefundCancellationReport = () => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [reportGenerated, setReportGenerated] = useState(false);

    // --- Validation: End cannot be before start ---
    const isDateRangeValid =
        fromDate &&
        toDate &&
        !isNaN(new Date(fromDate)) &&
        !isNaN(new Date(toDate)) &&
        toDate >= fromDate;

    const fetchReport = async () => {
        // Validation
        if (!fromDate || !toDate) {
            toast.error("Please select both start and end dates.");
            return;
        }
        if (toDate < fromDate) {
            toast.error("End Date cannot be before Start Date.");
            return;
        }
        setLoading(true);
        try {
            const response = await reportsAndAnalyticsApi.getUserReport({
                type: "refunds",
                startDate: fromDate ? toUTCStart(new Date(fromDate)).toISOString() : undefined,
                endDate: toDate ? toUTCEnd(new Date(toDate)).toISOString() : undefined,
            });
            setReportData(response?.data?.data?.result || { allCancelledOrders: [] });
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to fetch refund report");
            console.error("Error fetching refund report:", error);
        } finally {
            setReportGenerated(true);
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFromDate(null);
        setToDate(null);
        setReportData(null);
        setReportGenerated(false);
    };

    const exportToExcel = () => {
        const exportData = (reportData?.allCancelledOrders || []).map((order) => ({
            "Order No.": order.orderNumber || "N/A",
            "Date": order.cancelDetails?.cancelDate
                ? new Date(order.cancelDetails.cancelDate).toLocaleDateString()
                : "N/A",
            "Reason": order.cancelDetails?.cancelReason || "N/A",
            "Status": order.cancelDetails?.refundStatus || "N/A",
            "Amount ": order.cancelDetails?.refundedAmount ?? 0,
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Refund & Cancellation Report");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, `RefundCancellationReport_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box p={3}>
                <Typography variant="h5" gutterBottom>
                    Refund & Cancellation Report
                </Typography>

                <Grid container spacing={2} alignItems="center" mb={2}>
                    <Grid item xs={12} sm={6} md={2}>
                        <DatePicker
                            label="From Date"
                            value={fromDate}
                            onChange={(val) => {
                                setFromDate(val);
                                if (toDate && val && toDate < val) setToDate(null);
                            }}
                            maxDate={new Date()}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <DatePicker
                            label="To Date"
                            value={toDate}
                            onChange={(val) => setToDate(val)}
                            minDate={fromDate || undefined}
                            maxDate={new Date()}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={fetchReport}
                            disabled={loading || !isDateRangeValid}
                            sx={{ height: 48 }}
                        >
                            {loading ? "Loading..." : "Generate Report"}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            onClick={handleReset}
                            sx={{ height: 48 }}
                        >
                            Reset
                        </Button>
                    </Grid>
                </Grid>

                {/* RESULTS */}
                {
                    reportData === null ? (
                        <Paper
                            elevation={2}
                            sx={{
                                mt: 4,
                                width: "100%",
                                minHeight: "35vh",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "#fafbfc",
                                boxSizing: "border-box",
                                p: { xs: 2, sm: 4 },
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Select a date range above and click <strong>Generate Report</strong> to view cancelled/refunded orders.
                            </Typography>
                           
                        </Paper>
                    ) : reportData?.allCancelledOrders?.length > 0 ? (
                        <>
                            <Button variant="contained" onClick={exportToExcel} sx={{ mb: 2 }}>
                                Export Excel
                            </Button>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Order No.</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Reason</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.allCancelledOrders.map((order, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{order.orderNumber || "N/A"}</TableCell>
                                                <TableCell>
                                                    {order.cancelDetails?.cancelDate
                                                        ? new Date(order.cancelDetails.cancelDate).toLocaleDateString()
                                                        : "N/A"}
                                                </TableCell>
                                                <TableCell>{order.cancelDetails?.cancelReason || "N/A"}</TableCell>
                                                <TableCell>{order.cancelDetails?.refundStatus || "N/A"}</TableCell>
                                                <TableCell>{order.cancelDetails?.refundedAmount ?? 0}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    ) : (
                        reportGenerated && (
                            <Typography sx={{ mt: 3 }} color="text.secondary" align="center">
                                No data found.
                            </Typography>
                        )
                    )
                }

            </Box>
        </LocalizationProvider>
    );
};

export default RefundCancellationReport;
